import React, { useEffect, useMemo, useState } from "react";
import { Loader2, CalendarClock, Phone, Mail, List, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { AdminShell } from "./AdminShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUSES = ["Requested", "Confirmed", "Done", "Cancelled"];
const STATUS_COLOR = { Requested: "#F2A900", Confirmed: "#2E9B4B", Done: "#123D73", Cancelled: "#D64545" };
const SLOT_LABEL = { morning: "Morning (9–12)", afternoon: "Afternoon (12–4)", evening: "Evening (4–8)" };
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function AdminMeetings() {
  const [meetings, setMeetings] = useState(null);
  const [view, setView] = useState("list");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const load = () => api.get("/admin/meetings").then((res) => setMeetings(res.data.meetings));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setMeetings((m) => m.map((x) => (x.id === id ? { ...x, status } : x)));
    await api.patch(`/admin/meetings/${id}`, { status });
    toast.success("Meeting updated.");
  };

  const byDate = useMemo(() => {
    const map = {};
    (meetings || []).forEach((m) => { (map[m.preferred_date] = map[m.preferred_date] || []).push(m); });
    return map;
  }, [meetings]);

  if (!meetings) return <AdminShell title="Meetings"><div className="grid place-items-center py-20 text-navy"><Loader2 className="animate-spin" /></div></AdminShell>;

  const toggle = (
    <div className="flex rounded-full border border-slate-200 bg-white p-1">
      <button data-testid="view-list" onClick={() => setView("list")}
        className={`rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors duration-200 ${view === "list" ? "bg-navy text-white" : "text-slate-500"}`}><List size={15} /> List</button>
      <button data-testid="view-calendar" onClick={() => setView("calendar")}
        className={`rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors duration-200 ${view === "calendar" ? "bg-navy text-white" : "text-slate-500"}`}><CalendarDays size={15} /> Calendar</button>
    </div>
  );

  // calendar grid
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = cursor.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const iso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <AdminShell title="Meeting Requests" actions={toggle}>
      {meetings.length === 0 ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center text-slate-400 card-shadow">
          <CalendarClock className="mx-auto text-slate-300 mb-3" /> No meeting requests yet.
        </div>
      ) : view === "list" ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {meetings.map((m) => (
            <div key={m.id} data-testid={`meeting-${m.id}`} className="rounded-2xl bg-white border border-slate-200 p-5 card-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading font-bold text-navy">{m.full_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{(m.created_at || "").slice(0, 10)}</div>
                </div>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide"
                  style={{ background: `${STATUS_COLOR[m.status]}1a`, color: STATUS_COLOR[m.status] }}>{m.status}</span>
              </div>
              <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm">
                <div className="font-semibold text-navy flex items-center gap-2"><CalendarClock size={15} className="text-gold" /> {m.preferred_date}</div>
                <div className="text-xs text-slate-500 mt-1">{SLOT_LABEL[m.time_slot] || m.time_slot}</div>
              </div>
              {m.note && <p className="mt-3 text-xs text-slate-500 italic">"{m.note}"</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`tel:${m.mobile}`} className="rounded-lg bg-navy text-white text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5"><Phone size={13} /> {m.mobile}</a>
                <a href={`mailto:${m.email}`} className="rounded-lg border border-slate-200 text-navy text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5"><Mail size={13} /> Email</a>
              </div>
              <div className="mt-3">
                <Select value={m.status} onValueChange={(v) => setStatus(m.id, v)}>
                  <SelectTrigger data-testid={`meeting-status-${m.id}`} className="rounded-lg h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 p-5 card-shadow">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50"><ChevronLeft size={16} /></button>
            <div className="font-heading font-bold text-navy" data-testid="cal-month">{monthLabel}</div>
            <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50"><ChevronRight size={16} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-slate-400 mb-1">
            {WEEKDAYS.map((w) => <div key={w}>{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} className="min-h-[86px]" />;
              const dayIso = iso(d);
              const items = byDate[dayIso] || [];
              const isToday = dayIso === todayIso;
              return (
                <div key={i} className={`min-h-[86px] rounded-lg border p-1.5 ${isToday ? "border-gold bg-gold/5" : "border-slate-100"}`}>
                  <div className={`text-xs font-semibold ${isToday ? "text-gold" : "text-slate-500"}`}>{d}</div>
                  <div className="mt-1 space-y-0.5">
                    {items.slice(0, 3).map((m) => (
                      <div key={m.id} title={`${m.full_name} · ${m.status}`} className="text-[10px] truncate rounded px-1 py-0.5 font-semibold"
                        style={{ background: `${STATUS_COLOR[m.status]}1a`, color: STATUS_COLOR[m.status] }}>{m.full_name}</div>
                    ))}
                    {items.length > 3 && <div className="text-[10px] text-slate-400">+{items.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            {STATUSES.map((s) => (
              <span key={s} className="flex items-center gap-1.5 text-slate-500">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLOR[s] }} /> {s}
              </span>
            ))}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
